import React, { useEffect, useMemo } from "react";

import { Accordion, ButtonStandard, SingleSelectInput, Skeleton, Text } from "@performyard/py-components";
import type { FunctionsResponse, PatchFunctionsRequest } from "@performyard/service-contracts";

import { useAdminSettingsHomeFunctions, useUpdateAdminSettingsHomeFunctions } from "../../../../api/hooks";
import { useDirtyForm } from "../hooks/useDirtyForm";

import { ADMIN_HOME_ACCORDION_CLASS_NAME, ADMIN_HOME_BUTTON_CLASS_NAME } from "./adminHomeStyles";

type FunctionControl = FunctionsResponse["functions"][number];
type Values = Record<string, FunctionControl["value"]>;

type Props = { isOpen: boolean; onToggle: () => void; canEdit?: boolean; companyId?: string };

const PerformYardFeaturesSection: React.FC<Props> = ({ isOpen, onToggle, canEdit = true, companyId }) => {
	const { data, isLoading } = useAdminSettingsHomeFunctions(companyId);

	const initial: Values = useMemo(() => {
		const out: Values = {};
		for (const fn of data?.functions ?? []) out[fn.key] = fn.value;
		return out;
	}, [data]);

	const { values, setField, discard, syncFromServer, isDirty, diff } = useDirtyForm<Values>(initial);

	useEffect(() => {
		if (data) syncFromServer(initial);
	}, [data, initial, syncFromServer]);

	const mutation = useUpdateAdminSettingsHomeFunctions(undefined, companyId);

	const onSave = () => {
		const updates = Object.entries(diff)
			.filter(([, v]) => v !== undefined)
			.map(([key, value]) => ({
				key: key as PatchFunctionsRequest["updates"][number]["key"],
				value: value as PatchFunctionsRequest["updates"][number]["value"],
			}));
		if (updates.length === 0) return;
		mutation.mutate({ updates });
	};

	return (
		<Accordion
			isOpen={isOpen}
			onToggle={onToggle}
			variant="standard"
			className={ADMIN_HOME_ACCORDION_CLASS_NAME}
			headerContent={
				<div className="flex flex-col">
					<Text
						size="paragraph-1"
						dangerouslySetClassName="text-[18px] font-semibold leading-6 text-neutral-9"
					>
						PerformYard Features
					</Text>
					<Text size="paragraph-3" dangerouslySetClassName="leading-4 text-neutral-9">
						Enable or disable specific features for your company in PerformYard.
					</Text>
				</div>
			}
		>
			{isLoading || !data ? (
				<Skeleton className="h-64" />
			) : (
				<div className="-mx-4 -my-3 flex flex-col gap-8 p-8">
					<div className="flex flex-col gap-4">
						{data.functions.map((fn) => (
							<div key={fn.key} className="flex w-[288px] flex-col gap-2">
								<Text
									size="paragraph-2"
									dangerouslySetClassName="whitespace-nowrap font-semibold text-neutral-9"
								>
									{fn.label}
								</Text>
								<SingleSelectInput<string>
									value={values[fn.key] ?? fn.value}
									options={fn.options}
									onChange={(next) => next && setField(fn.key, next as FunctionControl["value"])}
									ariaLabel={`${fn.label} status`}
									disabled={!canEdit || fn.disabled}
								/>
								{fn.helpText ? (
									<Text size="paragraph-3" dangerouslySetClassName="leading-4 text-neutral-7">
										{fn.helpText}
									</Text>
								) : null}
							</div>
						))}
					</div>
					<div className="flex items-center gap-2">
						<ButtonStandard
							onClick={onSave}
							disabled={!canEdit || !isDirty || mutation.isPending}
							dangerouslySetClassName={ADMIN_HOME_BUTTON_CLASS_NAME}
						>
							Save Changes
						</ButtonStandard>
						{isDirty ? (
							<ButtonStandard
								variant="outline"
								onClick={discard}
								dangerouslySetClassName={ADMIN_HOME_BUTTON_CLASS_NAME}
							>
								Discard
							</ButtonStandard>
						) : null}
					</div>
				</div>
			)}
		</Accordion>
	);
};

export default PerformYardFeaturesSection;
